
import useAuth from "src/hooks/useAuth";
import { createContext, ReactNode } from "react";
import { BrokerProcessMonitor, ExecutionLog, ExecutionStatus } from "src/@type/broker-hub/broker-hub";
import { BrokerProcessMonitorDTO, ExecutionLogDTO, ExecutionStatusDTO } from "./DTO/DTO";
import { mapperBrokerProcessMonitorDTOToModel, mapperExecutionLogDTOToModel, mapperExecutionStatusDTOToModel } from "./DTO/DTOToModel";

type BrokerHubDataContextType = {
    getBrokerProcessMonitor: () => Promise<BrokerProcessMonitor[]>
    getExecutionStatuses: () => Promise<ExecutionStatus[]>
    getExecutionLog: (executionID: string) => Promise<ExecutionLog[]>
    resolveExecutionLog: (executionLog: ExecutionLog) => Promise<ExecutionLog>
};

export const BrokerHubDataContext = createContext<BrokerHubDataContextType | null>(null);

type BrokerHubDataContextProviderProps = {
    children: ReactNode;
};

export function BrokerHubDataContextProvider({ children }: BrokerHubDataContextProviderProps) {
    const { axios } = useAuth();

    const getBrokerProcessMonitor = () => new Promise<BrokerProcessMonitor[]>((resolve, reject) => {
        axios.get('/mid/broker-hub/process-monitor')
            .then(response => {
                const { data, error } = response.data;
                if (error) reject(error);
                else resolve((data as BrokerProcessMonitorDTO[]).map(mapperBrokerProcessMonitorDTOToModel));
            });
    });

    const getExecutionLog = (executionID: string) => new Promise<ExecutionLog[]>((resolve, reject) => {
        axios.get('/mid/broker-hub/process-status/execution-log', {
            params: {
                executionID: executionID
            }
        })
            .then(response => {
                const { data, error } = response.data;
                if (error) reject(error);
                else resolve((data as ExecutionLogDTO[]).map(mapperExecutionLogDTOToModel));
            });
    });

    const resolveExecutionLog = (processLog: ExecutionLog) => new Promise<ExecutionLog>((resolve, reject) => {
        axios.post('/mid/broker-hub/process-status/execution-log', processLog)
            .then(response => {
                const { data, error } = response.data;
                if (error) reject(error);
                else resolve(mapperExecutionLogDTOToModel(data as ExecutionLogDTO));
            });
    });

    const getExecutionStatuses = () => new Promise<ExecutionStatus[]>((resolve, reject) => {
        axios.get('/mid/broker-hub/process-status')
            .then(response => {
                const { data, error } = response.data;
                if (error) reject(error);
                else resolve((data as ExecutionStatusDTO[]).map(mapperExecutionStatusDTOToModel));
            });
    });

    return (
        <BrokerHubDataContext.Provider
            value={{
                getBrokerProcessMonitor,
                getExecutionStatuses,
                getExecutionLog,
                resolveExecutionLog
            }}
        >
            {children}
        </BrokerHubDataContext.Provider>
    );
}